import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { AbsenceReportDTO } from '@app/shared/services/dtos/reports/absence-report.dto';
import { SelectedTimeReportTypeEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import { PROFFEO_CHART_COLORS, resolveProffeoChartColor } from '@app/shared/ui/tokens/chart.tokens';
import moment from 'moment';
import {
  ABSENCE_TYPE_CONFIG,
  ABSENCE_TYPE_LABELS,
  AbsenceTypeConfig,
  AbsenceTypeEnum
} from '../models/absence-type-config';
import { ChartDataModel, ChartDatasetModel } from '../models/chart.models';

@Injectable()
export class AbsenceChartDataFormatterService {
  private readonly datePipe = inject(DatePipe);

  public getChartDataByMode(
    mode: SelectedTimeReportTypeEnum,
    filteredReports: AbsenceReportDTO[][],
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): ChartDataModel {
    const labels = this.getLabelsForMode(mode, selectedYear, selectedMonth, selectedWeek, selectedDay);
    const dates = this.getDatesForMode(mode, selectedYear, selectedMonth, selectedWeek, selectedDay);
    const dateComparison: 'day' | 'month' = mode === SelectedTimeReportTypeEnum.YEAR ? 'month' : 'day';

    const datasets = this.createDatasets(filteredReports, dates, dateComparison);

    return { labels, datasets };
  }

  public getRangeChartData(filteredReports: AbsenceReportDTO[][]): ChartDataModel {
    return {
      labels: [
        'Total',
        ABSENCE_TYPE_LABELS.HOLIDAY,
        ABSENCE_TYPE_LABELS.SICK_LEAVE,
        ABSENCE_TYPE_LABELS.SICK_LEAVE_CHILD,
        ABSENCE_TYPE_LABELS.PUBLIC_HOLIDAY,
        ABSENCE_TYPE_LABELS.OVERTIME_MINUS,
        ABSENCE_TYPE_LABELS.HOURS_BANK_MINUS,
        ABSENCE_TYPE_LABELS.OTHER
      ],
      datasets: [
        {
          label: 'Absence Hours',
          data: [
            this.sumAllAbsenceReports(filteredReports),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.HOLIDAY),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.SICK_LEAVE),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.SICK_LEAVE_CHILD),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.PUBLIC_HOLIDAY),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.OVERTIME_MINUS),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.HOURS_BANK_MINUS),
            this.sumAbsenceByType(filteredReports, AbsenceTypeEnum.OTHER)
          ],
          backgroundColor: resolveProffeoChartColor(PROFFEO_CHART_COLORS.publicHoliday),
          borderWidth: 1
        }
      ]
    };
  }

  private createDatasets(
    filteredReports: AbsenceReportDTO[][],
    dates: Date[],
    dateComparison: 'day' | 'month'
  ): ChartDatasetModel[] {
    const datasets: ChartDatasetModel[] = [];

    const absenceTypes = [
      AbsenceTypeEnum.HOLIDAY,
      AbsenceTypeEnum.SICK_LEAVE,
      AbsenceTypeEnum.SICK_LEAVE_CHILD,
      AbsenceTypeEnum.PUBLIC_HOLIDAY,
      AbsenceTypeEnum.OVERTIME_MINUS,
      AbsenceTypeEnum.HOURS_BANK_MINUS,
      AbsenceTypeEnum.OTHER
    ];

    absenceTypes.forEach(type => {
      datasets.push(this.createDatasetForAbsenceType(filteredReports, dates, type, dateComparison));
    });

    return datasets;
  }

  private createDatasetForAbsenceType(
    filteredReports: AbsenceReportDTO[][],
    dates: Date[],
    absenceType: AbsenceTypeEnum,
    dateComparison: 'day' | 'month'
  ): ChartDatasetModel {
    const config = this.getAbsenceTypeConfig(absenceType);
    const data = this.sumTimeForDatesAndAbsenceType(filteredReports, dates, absenceType, dateComparison);

    return {
      label: config.label,
      data,
      backgroundColor: config.color,
      barPercentage: 0.8,
      categoryPercentage: 0.8
    };
  }

  private sumTimeForDatesAndAbsenceType(
    filteredReports: AbsenceReportDTO[][],
    dates: Date[],
    absenceType: AbsenceTypeEnum,
    dateComparison: 'day' | 'month'
  ): number[] {
    return dates.map(date => {
      const reportsForDate = filteredReports.flat().filter(report => {
        const reportDate = moment(report.date);
        const targetDate = moment(date);

        if (dateComparison === 'month') {
          return (
            reportDate.year() === targetDate.year() &&
            reportDate.month() === targetDate.month() &&
            report.absenceType === absenceType
          );
        } else {
          return reportDate.isSame(targetDate, 'day') && report.absenceType === absenceType;
        }
      });

      const totalDuration = reportsForDate.reduce((sum, report) => {
        return sum + moment.duration(report.duration).asHours();
      }, 0);

      return totalDuration;
    });
  }

  private getAbsenceTypeConfig(type: AbsenceTypeEnum): AbsenceTypeConfig {
    const config = ABSENCE_TYPE_CONFIG[type];
    return { ...config, color: resolveProffeoChartColor(config.color) };
  }

  private getLabelsForMode(
    mode: SelectedTimeReportTypeEnum,
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): string[] {
    moment.locale('en', { week: { dow: 1 } });

    switch (mode) {
      case SelectedTimeReportTypeEnum.DAY:
        return [
          this.datePipe.transform(
            moment({ year: selectedYear, month: selectedMonth, day: selectedDay }).toDate(),
            'd MMM y'
          ) || ''
        ];

      case SelectedTimeReportTypeEnum.WEEK:
        return moment.weekdaysShort(true);

      case SelectedTimeReportTypeEnum.MONTH: {
        const daysInMonth = moment({ year: selectedYear, month: selectedMonth }).daysInMonth();
        return Array.from({ length: daysInMonth - 1 }, (_, i) => (i + 1).toString());
      }

      case SelectedTimeReportTypeEnum.YEAR:
        return moment.monthsShort();

      default:
        return [];
    }
  }

  private getDatesForMode(
    mode: SelectedTimeReportTypeEnum,
    selectedYear: number,
    selectedMonth: number,
    selectedWeek: number,
    selectedDay: number
  ): Date[] {
    const dates: Date[] = [];

    switch (mode) {
      case SelectedTimeReportTypeEnum.DAY:
        dates.push(moment({ year: selectedYear, month: selectedMonth, day: selectedDay }).toDate());
        break;

      case SelectedTimeReportTypeEnum.WEEK: {
        const startOfWeek = moment().year(selectedYear).week(selectedWeek).startOf('isoWeek');
        for (let i = 0; i < 7; i++) {
          dates.push(startOfWeek.clone().add(i, 'days').toDate());
        }
        break;
      }

      case SelectedTimeReportTypeEnum.MONTH: {
        const startOfMonth = moment({ year: selectedYear, month: selectedMonth }).startOf('month');
        const endOfMonth = moment({ year: selectedYear, month: selectedMonth }).endOf('month').date();
        for (let i = 1; i < endOfMonth; i++) {
          dates.push(startOfMonth.clone().add(i, 'days').toDate());
        }
        break;
      }

      case SelectedTimeReportTypeEnum.YEAR: {
        const startOfYear = moment({ year: selectedYear }).startOf('year');
        for (let i = 1; i < 12; i++) {
          dates.push(startOfYear.clone().add(i, 'months').toDate());
        }
        break;
      }
    }

    return dates;
  }

  private sumAllAbsenceReports(filteredReports: AbsenceReportDTO[][]): number {
    return filteredReports.flat().reduce((sum, report) => {
      return sum + moment.duration(report.duration).asHours();
    }, 0);
  }

  private sumAbsenceByType(filteredReports: AbsenceReportDTO[][], absenceType: AbsenceTypeEnum): number {
    return filteredReports
      .flat()
      .filter(report => report.absenceType === absenceType)
      .reduce((sum, report) => {
        return sum + moment.duration(report.duration).asHours();
      }, 0);
  }
}
